import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Building2, Users, Target, Heart, MapPin, Mail } from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';

const FOUNDERS = [
  {
    initials: 'LM',
    name: 'Lenni Marttila',
    role: 'Yhteisperustaja',
    focus: 'Tekoäly, visio, konseptointi ja yrityksen kehittäminen',
  },
  {
    initials: 'AO',
    name: 'Anton Ovaskainen',
    role: 'Yhteisperustaja',
    focus: 'AI-brändäys, brändipersoonat ja asiakaslähtöinen konseptointi',
  },
  {
    initials: 'ET',
    name: 'Eetu Tuominen',
    role: 'Yhteisperustaja',
    focus: 'Teknologia, toteutukset ja ratkaisujen rakentaminen',
  },
];

const Meista = () => {
  const { language } = useLanguage();
  usePageMeta({
    title: 'Agencive – Meistä',
    description:
      'Agencive on brändiagenttistudio, joka rakentaa yrityksille brändipersoonia sosiaaliseen mediaan ja verkkosivun AI-agenttiin.',
    path: '/meista',
  });

  return (
    <main className="min-h-screen bg-navy text-white">
      {/* Hero Section */}
      <section className="py-20 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-teal/10 via-transparent to-navy"></div>

        <div className="max-w-4xl mx-auto relative z-10">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-teal/20 rounded-full mb-6">
            <Building2 className="w-4 h-4 text-teal" />
            <span className="text-teal text-sm font-semibold">Tietoa meistä</span>
          </div>

          <h1 className="text-5xl md:text-6xl font-bold mb-6 text-white">
            {language === 'fi' ? 'Meistä' : 'About Us'}
          </h1>

          <p className="text-xl text-white/70 leading-relaxed">
            Agencive AI Oy on brändiagenttistudio, joka rakentaa yrityksille tunnistettavan
            brändipersoonan ja siihen perustuvan AI-agentin. Persoona toimii samana
            somesisällöissä ja verkkosivun agentissa.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 mb-12">
            <div className="flex items-center space-x-3 mb-6">
              <Target className="w-8 h-8 text-teal" />
              <h2 className="text-3xl font-bold text-white">Missiomme</h2>
            </div>
            <p className="text-white/70 leading-relaxed mb-4">
              Haluamme tehdä brändipersoonasta konkreettisen työkalun pk-yrityksille.
              Sama persoona herättää kiinnostuksen somesisällöissä ja jatkaa asiakkaan
              auttamista verkkosivulla — ilman että kokemus katkeaa kanavien välillä.
            </p>
            <p className="text-white/70 leading-relaxed mb-4">
              Rakennamme jokaisen brändipersoonan ja siihen perustuvan agentin
              asiakkaan omista lähtökohdista. Emme lupaa yleisiä valmispaketteja,
              vaan sovimme aina yhdessä, mitkä ominaisuudet ja integraatiot
              kuuluvat toimitukseen.
            </p>
            <p className="text-white/70 leading-relaxed">
              Työskentelemme läpinäkyvästi ja kerromme suoraan, mikä on realistista
              nykyisillä työkaluilla ja aikataululla.
            </p>
          </div>

          {/* Values */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 text-center">
              <Heart className="w-10 h-10 text-teal mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-3 text-white">Asiakaslähtöisyys</h3>
              <p className="text-white/60 text-sm">
                Aloitamme aina asiakkaan tavoitteista ja rakennamme ratkaisun,
                joka istuu juuri sinun brändiisi.
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 text-center">
              <Users className="w-10 h-10 text-teal mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-3 text-white">Läpinäkyvyys</h3>
              <p className="text-white/60 text-sm">
                Kerromme selkeästi, mitä toimitamme ja mikä jää toimituksen ulkopuolelle.
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 text-center">
              <Target className="w-10 h-10 text-teal mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-3 text-white">Iteratiivisuus</h3>
              <p className="text-white/60 text-sm">
                Aloitetaan pienestä, mitataan ja hiotaan agenttia oikean käytön perusteella.
              </p>
            </div>
          </div>

          {/* Team / Founders */}
          <div className="mt-12 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
            <h2 className="text-3xl font-bold text-white mb-6">Tiimimme</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {FOUNDERS.map((p) => (
                <div key={p.name} className="rounded-xl bg-white/5 border border-white/10 p-6">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-teal text-navy font-bold">
                      {p.initials}
                    </span>
                    <div>
                      <div className="font-semibold text-white">{p.name}</div>
                      <div className="text-white/60 text-sm">{p.role}</div>
                    </div>
                  </div>
                  <p className="text-white/70 text-sm leading-relaxed mt-4">
                    {p.focus}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Mid-page CTA */}
          <div
            className="mt-12 rounded-2xl bg-teal/10 border border-teal/30 p-7 md:p-9 grid md:grid-cols-12 gap-6 items-center"
            data-testid="meista-mid-cta"
          >
            <div className="md:col-span-8">
              <h2 className="text-2xl font-bold text-white">Haluatko keskustella, miten voisimme auttaa?</h2>
              <p className="mt-2 text-white/80">
                Aloitetaan 20 minuutin maksuttomalla kartoituksella. Saat arvion sopivasta
                käyttötapauksesta, toteutuksesta ja seuraavista askelista.
              </p>
            </div>
            <div className="md:col-span-4 md:flex md:justify-end">
              <a
                href="/yhteys?interest=brand-agent#contact-form"
                data-testid="meista-mid-cta-button"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-teal hover:bg-teal-light text-navy font-medium transition-colors"
              >
                Varaa maksuton kartoitus
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </a>
            </div>
          </div>

          {/* Company Info */}
          <div className="mt-12 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
            <h2 className="text-3xl font-bold text-white mb-6">Yritystiedot</h2>
            <div className="space-y-6 text-white/70">
              <p className="leading-relaxed">
                Agencive AI Oy on Suomessa rekisteröity osakeyhtiö, jonka kotipaikka on Lempäälä.
                Palvelemme asiakkaita etäyhteydellä koko Suomessa ja sovimme fyysiset tapaamiset
                erikseen tarpeen mukaan.
              </p>

              <div className="grid md:grid-cols-2 gap-6 pt-4">
                <div className="flex items-start space-x-3">
                  <Building2 className="w-6 h-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-white mb-1">Yritysmuoto</h3>
                    <p className="text-white/60">Agencive AI Oy (Osakeyhtiö)</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <MapPin className="w-6 h-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-white mb-1">Kotipaikka</h3>
                    <p className="text-white/60">Lempäälä, Suomi</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Users className="w-6 h-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-white mb-1">Y-tunnus</h3>
                    <p className="text-white/60">3590217-8</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Mail className="w-6 h-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-white mb-1">Yhteydenotot</h3>
                    <p className="text-white/60">agencive.ai@gmail.com</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Story Section */}
          <div className="mt-12">
            <h2 className="text-3xl font-bold text-white mb-6">Tarinamme</h2>
            <div className="space-y-4 text-white/70 leading-relaxed">
              <p>
                Agencive AI syntyi havainnosta, että brändi ja tekoäly puhuvat harvoin
                samaa kieltä. Chatbotit tuntuvat geneerisiltä, ja tunnistettava brändipersoona
                jää usein irralleen sivuston toiminnasta.
              </p>
              <p>
                Halusimme rakentaa yhdistelmän: saman persoonan, joka herättää kiinnostuksen
                somesisällöissä ja auttaa kävijää verkkosivulla. Näin yritys näkyy yhtenäisenä
                kaikissa kohtaamisissa — ei kahdessa erillisessä maailmassa.
              </p>
              <p>
                Kehitämme palvelua iteratiivisesti asiakkaidemme kanssa. Julkaisemme vain sen,
                mitä pystymme oikeasti toimittamaan, ja kerromme etukäteen, mikä jää toimituksen
                ulkopuolelle.
              </p>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-12 text-center">
            <a
              href="/yhteys?interest=brand-agent#contact-form"
              data-testid="meista-bottom-cta"
              className="inline-flex items-center space-x-2 px-8 py-4 bg-teal hover:bg-teal-light text-navy font-bold rounded-xl transition-all duration-300"
            >
              <span>Varaa maksuton kartoitus</span>
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Meista;
